import { FoodDetailsContent, FoodDetailsInfo, FoodDetailsBody } from "./styled"
import { Fragment, useState } from "react"
import Button from "components/Buttons/Button"
import PopConfirm from "components/PopConfirm"
import { useSetPageTitle } from "libs/ga"
import FoodImage from "components/foodDetails/share/FoodImage"
import { FOOD_DETAILS_BODY_ID } from "config"
import useScheduleFormSection from "features/schedule/hooks/useScheduleFormSection"
import { useCreateSchedule } from "features/schedule/hooks/CRUDSchedule"
import ScheduleConfigFormSection from "components/foodDetails/share/ScheduleConfigFormSection"
import { validateScheduleConfig } from "utils/validator"
import MealDetailsHeader from "./MealDetailsHeader"
import MealIngredients from "./MealIngredients"
import {
  isAIScanMeal,
  isAIScanNutritionFactsMeal,
  isAIScanPackageMeal,
  isEdamaMeal,
  isMyMeal,
  isOpenFoodFactMeal,
} from "model/food/utils/typeGuards/mealTypeGuards"
import { MealType } from "model/food/types"
import MyFoodNameAndPortion from "components/foodDetails/share/MyFoodNameAndPortion"
import EdamaNameAndPortion from "components/foodDetails/share/EdamaNameAndPortion"
import OpenFoodFactNameAndPortion from "components/foodDetails/share/OpenFoodFactNameAndPortion"
import OpenFoodFactDescription from "components/foodDetails/share/OpenFoodFactDescription"
import AIScanNameAndPortion from "components/foodDetails/share/AIScanNameAndPortion"
import useMealDetailsForm from "features/meal/hooks/logMeal/useMealDetailsForm"
import useLogMealFromMeal from "features/meal/hooks/logMeal/useLogMealFromMeal"
import { useBackToHome } from "features/meal/hooks"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import EditableNutrientInformation from "components/foodDetails/share/EditableNutrientInformation"
import EditNutrientForm from "components/foodDetails/share/EditNutrientForm"

const MealDetails = ({
  mealDetails,
  onBack,
  title = "Meal Details",
  logMealSuccessCallback,
}: {
  mealDetails: MealType
  onBack: () => void
  title?: string
  logMealSuccessCallback?: () => void
}) => {
  useSetPageTitle(title)
  const [displaySection, setDisplaySection] = useState<
    "MEAL_FORM" | "EDIT_NUTRITION_FORM"
  >("MEAL_FORM")

  const {
    meal,
    onPortionChange,
    onUnitChange,
    onMealTypeChange,
    updateFoodAsManualNutrition,
  } = useMealDetailsForm({ initMeal: mealDetails, mealType: mealDetails.type })

  const [isPortionValid, setIsPortionValid] = useState(true)

  const handlePortionChange = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }
  const backToHome = useBackToHome()

  const onLogMealSuccess = logMealSuccessCallback ?? backToHome

  const { handleLogMealFromMeal, logMealLoading } =
    useLogMealFromMeal(onLogMealSuccess)

  const [isPopConfirmDiscardOpen, setIsPopConfirmDiscardOpen] = useState(false)

  const [hasSchedule, setHasSchedule] = useState(false)
  const [isPopConfirmScheduleOpen, setIsPopConfirmScheduleOpen] =
    useState(false)

  const {
    onDayOfWeekChange,
    onSchedulePeriodChange,
    onScheduleTypeChange,
    scheduleConfig,
    isNoEndDate,
    onNoEndDateChange,
    getCreateSchedulePayloadFromMeal,
  } = useScheduleFormSection()

  const { handleCreateSchedule, createScheduleLoading } =
    useCreateSchedule(onLogMealSuccess)

  const handleSubmitMeal = (meal: MealType) => {
    if (hasSchedule) {
      const schedulePayload = getCreateSchedulePayloadFromMeal(meal)
      if (schedulePayload) {
        handleCreateSchedule(schedulePayload)
      }
    } else {
      handleLogMealFromMeal(meal)
    }
  }

  const isScheduleValid = hasSchedule
    ? validateScheduleConfig(scheduleConfig, !isNoEndDate)
    : true

  const hasIngredients = meal && (isMyMeal(meal) || isAIScanMeal(meal))

  if (!meal) {
    return
  }

  if (displaySection === "EDIT_NUTRITION_FORM" && meal) {
    return (
      <EditNutrientForm
        onSubmit={updateFoodAsManualNutrition}
        meal={meal}
        onClose={() => {
          setDisplaySection("MEAL_FORM")
        }}
      />
    )
  }

  return (
    <Fragment>
      <FoodDetailsContent>
        <FoodDetailsBody id={FOOD_DETAILS_BODY_ID}>
          <FoodImage previewImageUrl={meal.previewImageUrl}>
            <MealDetailsHeader
              onBack={onBack}
              mealType={meal.type}
              onMealTypeChange={(value) => {
                onMealTypeChange(value)
              }}
            />
          </FoodImage>

          <FoodDetailsInfo>
            {isMyMeal(meal) && (
              <MyFoodNameAndPortion
                food={meal}
                portionValue={meal.portion}
                unitValue={meal.unit}
                onPortionChange={handlePortionChange}
                onUnitChange={onUnitChange}
                setIsPortionValid={setIsPortionValid}
                calories={meal.calorie}
                gram={meal.gram}
              />
            )}

            {(isAIScanMeal(meal) ||
              isAIScanPackageMeal(meal) ||
              isAIScanNutritionFactsMeal(meal)) && (
              <AIScanNameAndPortion
                food={meal}
                portionValue={meal.portion}
                unitValue={meal.unit}
                onPortionChange={handlePortionChange}
                onUnitChange={onUnitChange}
                setIsPortionValid={setIsPortionValid}
                calories={meal.calorie}
                gram={meal.gram}
              />
            )}

            {isEdamaMeal(meal) && (
              <EdamaNameAndPortion
                edama={meal}
                portionValue={meal.portion}
                unitValue={meal.unit}
                onPortionChange={handlePortionChange}
                onUnitChange={onUnitChange}
                setIsPortionValid={setIsPortionValid}
                calories={meal.calorie}
                gram={meal.gram}
                unitOptions={meal.units}
              />
            )}

            {isOpenFoodFactMeal(meal) && (
              <OpenFoodFactNameAndPortion
                food={meal}
                portionValue={meal.portion}
                unitValue={meal.unit}
                onPortionChange={handlePortionChange}
                setIsPortionValid={setIsPortionValid}
                calories={meal.calorie}
                gram={meal.gram}
              />
            )}

            <EditableNutrientInformation
              entry={meal}
              setEditMode={() => setDisplaySection("EDIT_NUTRITION_FORM")}
            />

            <ScheduleConfigFormSection
              scheduleConfig={scheduleConfig}
              onDayOfWeekChange={onDayOfWeekChange}
              onSchedulePeriodChange={onSchedulePeriodChange}
              onScheduleTypeChange={onScheduleTypeChange}
              isNoEndDate={isNoEndDate}
              onNoEndDateChange={onNoEndDateChange}
              hasSchedule={hasSchedule}
              setHasSchedule={setHasSchedule}
            />

            {hasIngredients ? (
              <MealIngredients ingredients={meal.ingredients} isManualNutrition={meal.isManualNutrition} />
            ) : undefined}

            {isOpenFoodFactMeal(meal) && <OpenFoodFactDescription />}
          </FoodDetailsInfo>
        </FoodDetailsBody>
        <AppStyleCtaBox
          style={{
            flexDirection: "row",
          }}
        >
          <Button
            loading={logMealLoading}
            $type="primary"
            style={{ width: "100%", padding: "12px 16px" }}
            disabled={!isPortionValid || !isScheduleValid}
            onClick={() => {
              if (hasSchedule) {
                setIsPopConfirmScheduleOpen(true)
              } else {
                handleSubmitMeal(meal)
              }
            }}
          >
            Log this item
          </Button>
        </AppStyleCtaBox>
      </FoodDetailsContent>

      <PopConfirm
        isOpen={isPopConfirmDiscardOpen}
        title="Unsaved changes?"
        cancelText="Discard"
        confirmText="Save changes"
        onCancel={() => onBack()}
        onConfirm={() => {
          setIsPopConfirmDiscardOpen(false)
          if (hasSchedule) {
            setIsPopConfirmScheduleOpen(true)
          } else {
            handleSubmitMeal(meal)
          }
        }}
        loading={logMealLoading}
      >
        <p style={{ textAlign: "center" }}>Do you want to save the changes?</p>
      </PopConfirm>

      <PopConfirm
        cancelText="No"
        confirmText="Yes"
        isOpen={isPopConfirmScheduleOpen}
        title="Add Scheduled Meal?"
        onCancel={() => {
          setIsPopConfirmScheduleOpen(false)
        }}
        onConfirm={() => {
          handleSubmitMeal(meal)
        }}
        type="info"
        loading={createScheduleLoading}
      >
        <div style={{ textAlign: "center" }}>
          This meal will be logged automatically on your selected days. Do you
          want to proceed?
        </div>
      </PopConfirm>
    </Fragment>
  )
}

export default MealDetails
